import { useParams, useNavigate } from "@tanstack/react-router";
import { trpcClient } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfigLabel } from "@/components/shared/ConfigLabel";
import { useQuery } from "@tanstack/react-query";

import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import { Link } from "@tanstack/react-router";

export default function ToolingItemViewPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();

  const { data: item, isLoading } = useQuery({
    queryKey: ["tooling", id],
    queryFn: () => trpcClient.tooling.getItem.query({ id: id as string }),
  });

  if (isLoading) {
    return (
      <DetailLoadingSkeleton
        breadcrumbs={[
          { label: "Công cụ, Dụng cụ", to: "/master/tooling" },
          { label: "Chi tiết" },
        ]}
      />
    );
  }

  if (!item) {
    return (
      <div className="w-full px-4 sm:px-6 py-4">
        <p className="text-destructive">Không tìm thấy Công cụ / Dụng cụ.</p>
      </div>
    );
  }

  return (
    <StandardDetailLayout
      entityType="tooling_item"
      entityId={item.id}
      title={item.name}
      breadcrumbs={[
        { label: "Công cụ / Dụng cụ", to: "/master/tooling" },
        { label: item.code }
      ]}
      actions={
        <Link to="/master/tooling/$id/edit" params={{ id: item.id }}>
          <Button>
            <Edit className="mr-2 h-4 w-4" />
            Sửa thông tin
          </Button>
        </Link>
      }
      timestamps={{
        created: item.createdAt,
        updated: item.updatedAt
      }}
    >

      <Card>
        <CardHeader>
          <CardTitle>Thông tin cơ bản</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Mã CCDC</p>
            <p className="text-base">{item.code}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Tên CCDC
            </p>
            <p className="text-base">{item.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Phân loại
            </p>
            <p className="text-base">
              {item.category ? (
                <ConfigLabel
                  categoryCode="TOOLING_CATEGORY"
                  value={item.category}
                />
              ) : (
                "—"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Đơn vị tính
            </p>
            <p className="text-base">
              {item.unit ? (
                <ConfigLabel categoryCode="UNIT_TOOLING" value={item.unit} />
              ) : (
                "Cái"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Trạng thái
            </p>
            <p className="text-base">{item.status}</p>
          </div>
          <div className="col-span-2">
            <p className="text-sm font-medium text-muted-foreground">Mô tả</p>
            <p className="text-base whitespace-pre-wrap">
              {item.description || "—"}
            </p>
          </div>
        </CardContent>
      </Card>
      {/* Thảo luận / Tệp đính kèm / Lịch sử cập nhật: EntityContextPanel của StandardDetailLayout */}
    </StandardDetailLayout>
  );
}
